import React from 'react';
import styled from 'styled-components';
import { selectLightTheme } from '@utils/themes';
import { Body5, Container } from '@ui';
import ChevronDown from '@assets/icons/svgs/ChevronDown';
import ChevronUp from '@assets/icons/svgs/ChevronUp';
import { smoothScrollToAnchor } from '@utils/application';
import { convertHexToRGBA } from '@utils/usefulFuncs';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';
import { ROUTES } from '@constants/routes';
import { SplashSectionWrapper } from './styles';
import { logUserAction } from '@services/application';

const BackgroundWrapper = styled.div`
  background: ${({ theme }) => theme.colorPalette.primary.oak};
`;
const Wrapper = styled.div`
  * {
    color: ${({ theme }) => selectLightTheme(theme).text};
  }

  h3 {
    font-size: 48px;
    line-height: 64px;
    letter-spacing: -3px;
  }

  @media (max-width: 710px) {
    h3 {
      font-size: 24px;
      line-height: 32px;
      letter-spacing: -1px;
    }
  }
`;
const UseCaseWrapper = styled.div`
  position: relative;
  z-index: 2;
`;
const UseCaseExpandedWrapper = styled.div`
  padding: 0px 24px 24px 24px;

  li {
    padding: 8px 0;
  }

  * {
    font-size: 18px !important;
    line-height: 24px !important;
  }

  @media (max-width: 710px) {
    * {
      font-size: 14px !important;
      line-height: 18px !important;
    }
  }
`;
const UseCaseTitle = styled.div`
  font-size: 18px;
  line-height: 60px;
  font-weight: bold;

  padding: 0 0 0 24px;

  cursor: pointer;

  border-radius: 5px;

  &:hover {
    background-color: ${({ theme }) => selectLightTheme(theme).border};
  }

  @media (max-width: 710px) {
    font-size: 16px !important;
    line-height: 48px !important;
  }
`;
const IconWrapper = styled.span`
  svg {
    margin-left: 16px;
    fill: ${({ theme }) => selectLightTheme(theme).text};
    height: 24px;
    width: 24px;
  }
`;
const CaseStudyButtonWrapper = styled.div`
  padding: 16px 0 0 0;

  * {
    font-size: 14px !important;
  }
`;

export const Separator = styled.div`
  border-top: 1px solid ${({ theme }) => convertHexToRGBA(theme.themeColors.text, 0.1)};
  padding-top: 0px;
  margin-top: 36px;

  opacity: 0.7;
`;

function LandingVideo() {
  const containerStyle = {
    position: 'relative',
    width: '100%',
    paddingTop: '56.25%', // 16:9 aspect ratio
    overflow: 'hidden',
  };
  const iframeStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    border: 0,
  };

  const videoId = 's0HDqtrIjNY';
  const src = `https://www.youtube.com/embed/${videoId}?rel=0&autoplay=0&modestbranding=1`;
  return (
    <div style={containerStyle}>
      <iframe
        style={iframeStyle}
        src={src}
        title="Deal Analysis and Memo Mode"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

function LandingCaseStudy({ pdfUrl, width = '100%', height = '600px' }) {
  const containerStyle = {
    width,
    height,
    overflow: 'hidden',
    border: '1px solid #e2e8f0',
  };

  const embedStyle = {
    width: '100%',
    height: '100%',
    border: 'none',
  };

  const onClick = () => {
    const url = ROUTES.signup + `?case-study=1`;
    window.open(url, '_blank');
  };

  return (
    <CaseStudyButtonWrapper>
      <SkeletonButton onClick={onClick}>Get Access To Case Study</SkeletonButton>
    </CaseStudyButtonWrapper>
  );

  return (
    <div style={containerStyle}>
      <embed src={pdfUrl} type="application/pdf" style={embedStyle} aria-label="Case Study PDF" />
      {/* Fallback link for browsers that don’t support embed */}
      <p style={{ textAlign: 'center', marginTop: '8px' }}>
        Download the case study{' '}
        <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
          here
        </a>
        .
      </p>
    </div>
  );
}

const UseCase = ({ targetId, title, children, openSection, onOpen, onClose }) => {
  const logOpen = (title) => {
    logUserAction({
      event_name: 'clicked use case',
      context: 'splash page',
      value: title,
    });
  };

  const handleClick = () => {
    onOpen(targetId);
    logOpen(title); // endpoint requires auth
    setTimeout(() => {
      smoothScrollToAnchor('#' + targetId, -80);
    }, 300);
  };

  const isOpen = openSection === targetId;

  return (
    <UseCaseWrapper>
      <UseCaseTitle onClick={isOpen ? onClose : handleClick}>
        <b>{title}</b>
        <IconWrapper>{isOpen ? <ChevronUp yViewBox={-6} /> : <ChevronDown yViewBox={-6} />}</IconWrapper>
      </UseCaseTitle>
      {isOpen ? (
        <UseCaseExpandedWrapper>
          {children}
          <Separator />
        </UseCaseExpandedWrapper>
      ) : null}
    </UseCaseWrapper>
  );
};
export const UseCases = () => {
  const [openSection, setOpenSection] = React.useState('equity');

  const onOpen = (title) => {
    setOpenSection(title);
  };
  const onClose = () => {
    setOpenSection(null);
  };
  return (
    <BackgroundWrapper>
      <SplashSectionWrapper>
        <Wrapper>
          <h3>Time + Cost Savings, Alpha, and Deal Flow</h3>
          <Container top={24}>
            <UseCase
              targetId={'equity'}
              title={'Equities & Credit'}
              openSection={openSection}
              onOpen={onOpen}
              onClose={onClose}
            >
              <a id={'#equity'} />
              <ul>
                <li>
                  <Body5>
                    <b>Instant access to market-moving info</b> - query SEC filings, earnings call transcripts, dealer
                    conferences, and more, with automated, seamless ingestion
                  </Body5>
                </li>

                <li>
                  <Body5>
                    <b>Automated updates on companies you cover</b> - receive AI-generated, custom insights within
                    minutes of new sources being released
                  </Body5>
                </li>

                <li>
                  <Body5>
                    <b>Use internal research and models as sources</b> - one-time and automatic ingestion of proprietary
                    firm sources (research, interviews, memos, IC docs) seamlessly integrated with public info
                  </Body5>
                </li>
              </ul>
              <Container>
                <LandingCaseStudy pdfUrl={'pdfs/Tornado Case Study - L_S Equity Fund.pdf'} />
              </Container>
            </UseCase>
            <UseCase
              targetId={'venture-capital'}
              title={'Venture Capital & Private Equity'}
              openSection={openSection}
              onOpen={onOpen}
              onClose={onClose}
            >
              <a id={'#venture-capital'} />

              <ul>
                <li>
                  <Body5>
                    <b>Customized, accurate analysis in minutes</b> - IC memos, deal summaries, company snapshots and
                    more in minutes rather than hours (or days)
                  </Body5>
                </li>

                <li>
                  <Body5>
                    <b>Effortless end-to-end workflows</b> - Tornado conforms to your firm's conventions for
                    calculations, screening criteria, final doc formatting, etc.
                  </Body5>
                </li>

                <li>
                  <Body5>
                    <b>One click to insight</b> - Generate templated, presentation-quality final outputs (Word, CSV,
                    PDF, etc) with one click
                  </Body5>
                </li>
              </ul>
              <Container>
                <LandingVideo />
              </Container>
            </UseCase>
            <UseCase
              targetId={'private-credit'}
              title={'Private Credit & Direct Lending'}
              openSection={openSection}
              onOpen={onOpen}
              onClose={onClose}
            >
              <a id={'#private-credit'} />
              <ul>
                <li>
                  <Body5>
                    <b>Data room to decision in minutes</b> - Textual and analytical processes turn unstructured data
                    and files into analyst/associate/VP level work product in minutes
                  </Body5>
                </li>

                <li>
                  <Body5>
                    <b>Specialized doc analysis</b> - Robust, accurate, immediate analysis of credit agreements, lender
                    earnings calls, indentures, etc.
                  </Body5>
                </li>
              </ul>
            </UseCase>
            <UseCase
              targetId={'investment-banking'}
              title={'Investment Banking'}
              openSection={openSection}
              onOpen={onOpen}
              onClose={onClose}
            >
              <a id={'#investment-banking'} />
              <ul>
                <li>
                  <Body5>
                    <b>AI-powered deal flow reports</b> - Instant, accurate, up-to-date summaries of 3,500+ public
                    companies - your AI-driven initiation report for any public company
                  </Body5>
                </li>

                <li>
                  <Body5>
                    <b>Daily update on your coverage universe</b> - AI reviews filings, disclosures, presentations,
                    earnings calls and more, surfacing only what’s important to you
                  </Body5>
                </li>

                <li>
                  <Body5>
                    <b>Complex financial analysis in seconds</b> - Go well beyond LLM capabilities with a truly
                    intelligent analyst that understands CIMs, PIMS, decks and spreadsheets, does math, and builds your
                    presentation just the way you like
                  </Body5>
                </li>
              </ul>
              <Container>
                <LandingCaseStudy pdfUrl={'pdfs/Tornado Case Study - Investment Banking (Nov 24).pdf'} />
              </Container>
            </UseCase>
            <UseCase
              targetId={'macro'}
              title={'Rates & Macro'}
              openSection={openSection}
              onOpen={onOpen}
              onClose={onClose}
            >
              <a id={'#macro'} />
              <ul>
                <li>
                  <Body5>
                    <b>Analyze nuances in Fed communication</b> - Search all Fed releases semantically or using specific
                    word or phrases - Tornado knows the difference and powers instant, detailed analysis with
                    attribution
                  </Body5>
                </li>

                <li>
                  <Body5>
                    <b>Custom sentiment analysis</b> - Speaker and sentence-level hawkish/dovish sentiment scoring on
                    any Fed source
                  </Body5>
                </li>

                <li>
                  <Body5>
                    <b>Live event transcription</b> - Read the Fed presser Q&A in realtime, comparing the language to
                    previous statements for immediate insights when they matter most
                  </Body5>
                </li>
              </ul>
            </UseCase>
          </Container>
        </Wrapper>
        <Container top={64} />
      </SplashSectionWrapper>
    </BackgroundWrapper>
  );
};
